import React, { useCallback, useMemo, useState } from 'react'
import { Outlet, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import DashboardSidebar from './DashboardSidebar'
import { getMainSidebarActiveNavId, navigateFromMainSidebarItem } from '../lib/mainShellNav'
import { useAuth } from '../lib/AuthContext'
import { canAccessAnyAdminArea, getFirstAccessibleAdminPath } from '../lib/dashboardNav'

/**
 * @typedef {{ show: boolean, node: React.ReactNode }} SidebarFooterState
 */

export default function MainShell() {
  const navigate = useNavigate()
  const location = useLocation()
  const { canAccessPage } = useAuth()
  const [searchParams] = useSearchParams()
  const [sidebarFooter, setSidebarFooter] = useState(
    /** @type {SidebarFooterState} */ ({ show: false, node: null })
  )

  const setMainSidebarFooter = useCallback((next) => {
    setSidebarFooter(next ?? { show: false, node: null })
  }, [])

  const tabParam = searchParams.get('tab')
  const activeItemId = useMemo(
    () => getMainSidebarActiveNavId(location.pathname, tabParam),
    [location.pathname, tabParam]
  )

  const showAdminPanelMenuItem = useMemo(() => canAccessAnyAdminArea(canAccessPage), [canAccessPage])

  const handleSidebarNav = useCallback(
    (id) => {
      navigateFromMainSidebarItem(id, navigate)
    },
    [navigate]
  )

  const handleAdminPanel = useCallback(() => {
    const path = getFirstAccessibleAdminPath(canAccessPage)
    if (path) navigate(path)
  }, [canAccessPage, navigate])

  const outletContext = useMemo(
    () => ({
      setMainSidebarFooter,
    }),
    [setMainSidebarFooter]
  )

  return (
    <div
      style={{
        display: 'flex',
        minHeight: '100vh',
        backgroundColor: 'var(--color-bg-body)',
        fontFamily: "'Inter', -apple-system, sans-serif",
      }}
    >
      <DashboardSidebar
        variant="main"
        activeItemId={activeItemId}
        onItemClick={handleSidebarNav}
        showQuickAddFooter={sidebarFooter.show}
        quickAddFooter={sidebarFooter.node}
        showAdminPanelMenuItem={showAdminPanelMenuItem}
        onAdminPanel={handleAdminPanel}
      />
      <div
        style={{
          marginLeft: 'var(--sidebar-width)',
          flex: '1 1 0%',
          minWidth: 0,
          width: '100%',
          minHeight: '100vh',
          backgroundColor: 'var(--color-bg-body)',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          overflowX: 'hidden',
          transition: 'margin-left 0.2s ease',
        }}
      >
        <Outlet context={outletContext} />
      </div>
    </div>
  )
}
